/**
 * /es/latinas-en-vivo — Spanish-language landing page for Latina performers
 * SEO target: "latinas en vivo", "chicas cam en vivo", "webcam latinas gratis"
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box, Typography, Button, Grid, Avatar, Chip, Skeleton,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { PreRollAd } from "../components/TwinredPreRoll";

// ─── SEO head helper ────────────────────────────────────────────────────────
const SEOHead = ({ title, description, keywords, canonical, lang }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        prop ? el.setAttribute("property", name) : el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (description) { setMeta("description", description); setMeta("og:description", description, true); }
    if (keywords) setMeta("keywords", keywords);
    if (title) setMeta("og:title", title, true);
    if (canonical) {
      let link = document.querySelector("link[rel='canonical']");
      if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
      link.href = canonical;
      setMeta("og:url", canonical, true);
    }
    // hreflang
    let hreflang = document.querySelector("link[rel='alternate'][hreflang='es']");
    if (!hreflang) {
      hreflang = document.createElement("link");
      hreflang.rel = "alternate";
      hreflang.setAttribute("hreflang", "es");
      document.head.appendChild(hreflang);
    }
    hreflang.href = canonical || "https://instacamgirls.com/es/latinas-en-vivo";
    // html lang
    document.documentElement.setAttribute("lang", lang || "es");
  }, [title, description, keywords, canonical, lang]);
  return null;
};

// ─── Styled ─────────────────────────────────────────────────────────────────
const blink = keyframes`0%{opacity:0}50%{opacity:1}100%{opacity:0}`;
const LiveDot = styled("span")({
  height: 9, width: 9, backgroundColor: "#4CAF50",
  borderRadius: "50%", display: "inline-block", marginRight: 6,
  animation: `${blink} 1.5s ease-in-out infinite`,
});

const PerformerTile = styled(Box)({
  position: "relative", borderRadius: 12, overflow: "hidden",
  cursor: "pointer", backgroundColor: "#111",
  "&:hover .overlay": { opacity: 1 },
  "&:hover img": { transform: "scale(1.05)" },
});

const Overlay = styled(Box)({
  position: "absolute", inset: 0, opacity: 0, transition: "opacity 0.2s",
  background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 60%)",
  display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 12,
});

// ─── FAQ data ────────────────────────────────────────────────────────────────
const FAQ = [
  {
    q: "¿Las latinas están realmente en vivo?",
    a: "Sí — cada modelo que ves está transmitiendo en tiempo real. Sin grabaciones, sin bots.",
  },
  {
    q: "¿Es gratis ver a las latinas en cámara?",
    a: "El chat gratuito siempre está disponible. Los shows privados requieren créditos.",
  },
  {
    q: "¿De qué países son las modelos latinas?",
    a: "La mayoría son de Colombia, México, Venezuela, Argentina, Brasil y República Dominicana.",
  },
  {
    q: "¿Puedo hablar en español con las modelos?",
    a: "Absolutamente — la mayoría de las modelos latinas hablan español como idioma principal.",
  },
  {
    q: "¿Cuántas latinas están en línea ahora mismo?",
    a: "Típicamente entre 80 y 150 modelos latinas están en vivo en cualquier momento del día.",
  },
];

// ─── Related categories ──────────────────────────────────────────────────────
const RELATED = [
  { slug: "latina-cam-girls",  label: "Latinas (EN)", emoji: "🌶️" },
  { slug: "brunette-cam-girls", label: "Morenas",     emoji: "🤎" },
  { slug: "milf-cam-girls",    label: "MILF",         emoji: "🔥" },
  { slug: "bbw-cam-girls",     label: "Curvy / BBW",  emoji: "💋" },
  { slug: "teen-cam-girls",    label: "Teen 18+",     emoji: "✨" },
];

// ─── Main component ──────────────────────────────────────────────────────────
const LatinasEnVivoPage = () => {
  const [performers, setPerformers] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [hasMore, setHasMore]       = useState(true);
  const [preRollPending, setPreRollPending] = useState(null);

  const fetchPerformers = async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const params = { ethnicity: "hispanic", language: "spanish", limit: 24, page: pageNum };
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, { params });
      let list = Array.isArray(res.data) ? res.data : [];

      // Fallback: if too few results with language filter, retry with ethnicity only
      if (list.length < 6 && pageNum === 1) {
        const fallback = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, {
          params: { ethnicity: "hispanic", limit: 24, page: pageNum },
        });
        list = Array.isArray(fallback.data) ? fallback.data : [];
      }

      if (list.length < 24) setHasMore(false);

      const mapped = list.map((p) => {
        const a = p["@attributes"] || {};
        const gallery = (() => {
          const raw = p?.Media?.PhotoGallery?.Pic;
          if (!raw) return [];
          return (Array.isArray(raw) ? raw : [raw])
            .map(pic => pic?.Full?.["@attributes"]?.Src || "").filter(Boolean);
        })();
        return {
          id: a.Id, name: a.Name, age: a.Age,
          ethnicity: a.Ethnicity, hairColor: a.HairColor,
          goldShow: !!(a.PerfFlag && (parseInt(a.PerfFlag) & 0x00100000)),
          thumb: p?.Media?.Pic?.Thumb?.["@attributes"]?.Src || "",
          full:  p?.Media?.Pic?.Full?.["@attributes"]?.Src  || "",
          gallery,
          about: p?.Descriptions?.About || "",
          chatRoomUrl: `https://instacamgirlslive.com/cam/${a.Name}/?smid=${a.Id}&AFNO=1-94033`,
        };
      });
      setPerformers(prev => append ? [...prev, ...mapped] : mapped);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPerformers([]); setPage(1); setHasMore(true);
    fetchPerformers(1, false);
  }, []);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPerformers(next, true);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      <SEOHead
        title="Latinas en Vivo — Chicas Cam en Directo | Instacamgirls"
        description="Ve a las latinas más ardientes en cámara ahora mismo. Más de 100 modelos de Colombia, México, Venezuela en chat gratis. Show privado disponible 24/7."
        keywords="latinas en vivo, chicas cam latinas, latinas webcam gratis, colombianas cam, mexicanas cam, venezolanas en vivo, cam latinas"
        canonical="https://instacamgirls.com/es/latinas-en-vivo"
        lang="es"
      />

      {preRollPending && (
        <PreRollAd onComplete={() => {
          const p = preRollPending;
          window.open(p.chatRoomUrl, "_blank");
          setPreRollPending(null);
        }} />
      )}

      {/* Hero header */}
      <Box sx={{
        background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        px: { xs: 2, md: 6 }, py: { xs: 4, md: 6 }, color: "#fff",
      }}>
        <Button
          component={Link} to="/"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2, color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
        >
          Inicio
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <LiveDot />
          <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.9 }}>EN VIVO AHORA</Typography>
          {performers.length > 0 && (
            <Chip
              label={`${performers.length}+ latinas online`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, border: "1px solid rgba(255,255,255,0.5)" }}
            />
          )}
        </Box>

        <Typography variant="h3" sx={{ fontWeight: 900, mb: 1.5, lineHeight: 1.2 }}>
          🌶️ Latinas en Vivo
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 640, fontWeight: 400, mb: 2 }}>
          Las chicas latinas más ardientes en cámara ahora mismo. Colombianas, mexicanas, venezolanas, brasileñas y más — chat gratis, show privado disponible.
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {["🇨🇴 Colombia", "🇲🇽 México", "🇻🇪 Venezuela", "🇧🇷 Brasil", "🇦🇷 Argentina", "🇩🇴 R. Dominicana"].map(c => (
            <Chip key={c} label={c} size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, border: "1px solid rgba(255,255,255,0.4)" }} />
          ))}
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ px: { xs: 1, md: 4 }, py: 3, maxWidth: 1400, mx: "auto" }}>
        <Grid container spacing={2}>
          {loading && performers.length === 0
            ? Array.from({ length: 12 }).map((_, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                </Grid>
              ))
            : performers.map((p) => (
                <Grid item xs={6} sm={4} md={3} key={p.id}>
                  <PerformerTile onClick={() => setPreRollPending(p)}>
                    <Box
                      component="img"
                      src={p.gallery[0] || p.full || p.thumb}
                      alt={`${p.name} latina cam en vivo`}
                      sx={{ width: "100%", height: 260, objectFit: "cover",
                        transition: "transform 0.3s ease", display: "block" }}
                      onError={(e) => { e.target.src = p.thumb || p.full || ""; }}
                    />
                    {/* Live badge */}
                    <Box sx={{
                      position: "absolute", top: 8, left: 8,
                      bgcolor: "rgba(76,175,80,0.9)", borderRadius: "12px",
                      px: 1, py: 0.3, display: "flex", alignItems: "center", gap: 0.5,
                    }}>
                      <LiveDot style={{ marginRight: 2 }} />
                      <Typography sx={{ color: "#fff", fontSize: "0.7rem", fontWeight: 700 }}>EN VIVO</Typography>
                    </Box>
                    {p.goldShow && (
                      <Chip label="🥇 GOLD" size="small" sx={{
                        position: "absolute", top: 8, right: 8,
                        bgcolor: "#FFD700", color: "#000", fontWeight: 700, fontSize: "0.65rem", height: 20,
                      }} />
                    )}
                    <Overlay className="overlay">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Avatar src={p.thumb} sx={{ width: 28, height: 28 }} />
                        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem" }}>{p.name}</Typography>
                      </Box>
                      {p.age && (
                        <Typography sx={{ color: "#ddd", fontSize: "0.75rem", mb: 1 }}>
                          {p.age} años{p.hairColor ? ` · cabello ${p.hairColor}` : ""}
                        </Typography>
                      )}
                      {p.about && (
                        <Typography sx={{ color: "#bbb", fontSize: "0.72rem", mb: 1, fontStyle: "italic" }}>
                          "{p.about.slice(0, 60)}…"
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" variant="contained"
                          sx={{ background: "linear-gradient(45deg,#f093fb,#f5576c)", borderRadius: "20px",
                            fontSize: "0.7rem", py: 0.5, px: 1.5 }}
                          onClick={(e) => { e.stopPropagation(); setPreRollPending(p); }}>
                          <LiveTvIcon sx={{ fontSize: 13, mr: 0.5 }} /> Ver
                        </Button>
                        <Button size="small" variant="contained"
                          sx={{ background: "linear-gradient(90deg,#7c3aed,#a855f7)", color: "#fff",
                            borderRadius: "20px", fontSize: "0.7rem", py: 0.5, px: 1.5,
                            "&:hover": { background: "linear-gradient(90deg,#6d28d9,#9333ea)" } }}
                          onClick={(e) => { e.stopPropagation(); window.open(p.chatRoomUrl, "_blank"); }}>
                          💬 Chat
                        </Button>
                      </Box>
                    </Overlay>
                  </PerformerTile>
                </Grid>
              ))}
        </Grid>

        {hasMore && !loading && performers.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button variant="contained"
              sx={{ background: "linear-gradient(45deg,#f093fb,#f5576c)", borderRadius: "50px", px: 5, py: 1.5, fontWeight: 700 }}
              onClick={loadMore}>
              Ver más latinas 🌶️
            </Button>
          </Box>
        )}

        {/* SEO copy block */}
        <Box sx={{ mt: 6, maxWidth: 800, mx: "auto", bgcolor: "#fff", borderRadius: 2, p: 4, border: "1px solid #eee" }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
            Chicas Latinas en Cámara — En Vivo Ahora
          </Typography>
          <Typography variant="body1" sx={{ color: "#555", mb: 2, lineHeight: 1.8 }}>
            Bienvenido a la sección latina de InstaCamGirls. Aquí encontrarás a las modelos latinas más
            populares transmitiendo en vivo ahora mismo — colombianas, mexicanas, venezolanas, brasileñas,
            argentinas y más. El chat es siempre gratuito. Si quieres una experiencia más íntima, puedes
            ir a privado en cualquier momento.
          </Typography>
          <Typography variant="body2" sx={{ color: "#777", lineHeight: 1.8 }}>
            Todas las modelos son mayores de 18 años, verificadas, y transmiten en alta definición. No
            se requiere registro para ver el chat gratuito. Los shows privados y las propinas están
            disponibles con créditos.
          </Typography>
        </Box>

        {/* FAQ */}
        <Box sx={{ mt: 5, maxWidth: 800, mx: "auto" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            Preguntas Frecuentes
          </Typography>
          {FAQ.map((item, i) => (
            <Box key={i} sx={{ mb: 3, pb: 3, borderBottom: "1px solid #eee" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{item.q}</Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>{item.a}</Typography>
            </Box>
          ))}
        </Box>

        {/* Related categories */}
        <Box sx={{ mt: 5, p: 3, bgcolor: "#fff", borderRadius: 2, border: "1px solid #eee" }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Ver también</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {RELATED.map(({ slug, label, emoji }) => (
              <Chip
                key={slug} label={`${emoji} ${label}`}
                component={Link} to={`/category/${slug}`} clickable
                sx={{ bgcolor: "#fff0f7", color: "#FF4196", border: "1px solid #FF4196",
                  fontWeight: 600, "&:hover": { bgcolor: "#FF4196", color: "#fff" } }}
              />
            ))}
            <Chip
              label="🌐 Ver todas las categorías"
              component={Link} to="/" clickable
              sx={{ bgcolor: "#f5f5f5", color: "#333", fontWeight: 600,
                "&:hover": { bgcolor: "#333", color: "#fff" } }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LatinasEnVivoPage;
